/**
 * Utilitários para a janela de 24h do WhatsApp.
 * Usado na lista de conversas e no header da conversa para sinalizar "prestes a expirar" ou "expirado".
 */

const WINDOW_HOURS = 24;
const WARNING_START_HOURS = 20;

/**
 * Calcula o estado da janela de 24h com base na data da última atividade (ou última mensagem do cliente).
 * @param {string | Date | number | null} lastDate - Data da última mensagem do cliente ou última atividade
 * @returns {{ status: 'ok' | 'aboutToExpire' | 'expired', hoursLeft: number | null, tooltip: string }}
 */
export function getWhatsAppWindowState(lastDate) {
  if (lastDate == null) {
    return {
      status: "ok",
      hoursLeft: null,
      tooltip: "",
    };
  }

  const date = typeof lastDate === "object" && lastDate instanceof Date
    ? lastDate
    : new Date(lastDate);

  if (Number.isNaN(date.getTime())) {
    return { status: "ok", hoursLeft: null, tooltip: "" };
  }

  const diffMs = Date.now() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < WARNING_START_HOURS) {
    const hoursLeft = Math.max(0, WINDOW_HOURS - diffHours);
    return {
      status: "ok",
      hoursLeft,
      tooltip: "",
    };
  }

  if (diffHours < WINDOW_HOURS) {
    const hoursLeft = Math.max(0, WINDOW_HOURS - diffHours);
    const rounded = Math.floor(hoursLeft);
    return {
      status: "aboutToExpire",
      hoursLeft,
      tooltip: `Janela de 24h do WhatsApp expira em aproximadamente ${rounded} ${rounded === 1 ? "hora" : "horas"}. Envie sua resposta ou use um template de reengajamento antes que a janela feche.`,
    };
  }

  return {
    status: "expired",
    hoursLeft: 0,
    tooltip: "A janela de 24 horas do WhatsApp está fechada. Para enviar mensagens, use um template de reengajamento no campo de mensagem ou aguarde o cliente enviar uma nova mensagem.",
  };
}

/**
 * Retorna a data da última mensagem do cliente a partir da lista de mensagens.
 * @param {Array<{ senderType?: string, sender?: string, updatedAt?: string, lastUpdate?: string, timestamp?: string, date?: string }>} messages
 * @returns {Date | null}
 */
export function getLastClientMessageDate(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;

  const clientMessages = messages.filter((msg) => {
    const senderType = (msg?.senderType || msg?.sender || "").toLowerCase();
    return (
      senderType === "client" ||
      senderType === "cliente" ||
      senderType === "customer" ||
      senderType === "user"
    );
  });

  if (clientMessages.length === 0) return null;

  let lastDate = null;
  for (const msg of clientMessages) {
    const ts = msg.updatedAt || msg.lastUpdate || msg.timestamp || msg.date;
    if (!ts) continue;
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime()) && (!lastDate || d > lastDate)) {
      lastDate = d;
    }
  }
  return lastDate;
}
